import $router from 'src/router'
import { LocalStorage } from 'src/services/localStorage/localStorage'
import { AuthAPI } from 'src/api/requests'
import { waitUntil } from 'src/utils/common/common'
import dayjs from 'dayjs'

export class Auth {
  static ACCESS_TOKEN_KEY = 'accessToken'
  static REFRESH_TOKEN_KEY = 'refreshToken'
  static IS_REFRESHING_KEY = 'tokenIsRefreshing'
  static REFRESH_TIMEOUT_SECONDS = 15

  static get isRefreshing() {
    const refreshStartTime = LocalStorage.get(this.IS_REFRESHING_KEY)
    if (!refreshStartTime) {
      return false
    }

    const elapsedTime = dayjs().diff(dayjs(refreshStartTime), 'seconds')
    return elapsedTime <= this.REFRESH_TIMEOUT_SECONDS
  }

  static set isRefreshing(value) {
    if (value) {
      LocalStorage.set(this.IS_REFRESHING_KEY, dayjs().toISOString())
    } else {
      LocalStorage.remove(this.IS_REFRESHING_KEY)
    }
  }

  static get accessToken() {
    return LocalStorage.get(this.ACCESS_TOKEN_KEY)
  }

  static set accessToken({ token, expires_in }) {
    LocalStorage.set(this.ACCESS_TOKEN_KEY, {
      token,
      expires_in: dayjs().add(expires_in, 'seconds').unix(),
    })
  }

  static get hasAccessToken() {
    if (!LocalStorage.has(this.ACCESS_TOKEN_KEY)) {
      return false
    }

    const { expires_in } = LocalStorage.get(this.ACCESS_TOKEN_KEY)
    const isNotExpired = dayjs().unix() < expires_in
    return isNotExpired
  }

  static get refreshToken() {
    return LocalStorage.get(this.REFRESH_TOKEN_KEY)
  }

  static set refreshToken(token) {
    LocalStorage.set(this.REFRESH_TOKEN_KEY, token)
  }

  static get hasRefreshToken() {
    return LocalStorage.has(this.REFRESH_TOKEN_KEY)
  }

  static setTokens({ access_token, expires_in, refresh_token }) {
    this.accessToken = { token: access_token, expires_in }
    this.refreshToken = refresh_token
  }

  static async refreshAccessToken() {
    const beforeUnloadHandler = () => {
      this.isRefreshing = false
    }
    window.addEventListener('beforeunload', beforeUnloadHandler)

    this.isRefreshing = true
    try {
      const data = await AuthAPI.refreshToken({
        refresh_token: this.refreshToken,
      })
      this.setTokens(data)
    } catch (e) {
      await this.logout()
    } finally {
      this.isRefreshing = false
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }

  static waitForRefreshingEnd() {
    return waitUntil(() => !this.isRefreshing, 50)
  }

  static handleAccessTokenRefresh() {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.isRefreshing) {
          await this.waitForRefreshingEnd()
        } else if (!this.hasAccessToken) {
          if (this.hasRefreshToken) {
            await this.refreshAccessToken()
          } else {
            await this.logout()
            return reject()
          }
        }

        resolve()
      } catch (e) {
        reject(e)
      }
    })
  }

  static async logout() {
    LocalStorage.remove(this.ACCESS_TOKEN_KEY)
    LocalStorage.remove(this.REFRESH_TOKEN_KEY)
    this.isRefreshing = false

    const router = $router()
    await router.replace({ name: 'login' })
    router.go()
  }
}
